/**
 * Laser-wall / sweep FX — the premium beam treatment for the `lasers` family
 * (the sweeping laser walls and the blinking cross-field sweep). *Core decides,
 * Phaser renders*: core owns the beam geometry, the sweep drift and the blink
 * timing; this paints it like the turret lance (layered glow + hot core + endpoint
 * flares + crackling sparks) instead of a flat rectangle.
 *
 * Phases, derived from the hazard's `bornMs`/`telegraphMs`/`activeMs` (so live play
 * and replays match):
 *  - TELEGRAPH `[bornMs, +telegraphMs)` — a thin charging sight-line fades in.
 *  - ACTIVE + ON  — a bright, pulsing red lance with endpoint flares + a crackle of
 *    sparks along the beam (the lethal beat).
 *  - ACTIVE + OFF (blink) — drops to a faint teal "primed" line (the safe-to-cross
 *    beat), matching the harmless window core grants on the blink's off beat.
 *
 * Decorative + pooled (one glow + core + two endpoint flares per live laser id, plus
 * one shared spark emitter); edge-consistent for replays.
 */
import Phaser from 'phaser';
import type { SegmentHazard } from '../core';
import { segmentBlinkOn } from '../core';
import { HAZARD_TEX, HAZARD_COLORS } from './textures';

/** Author-time pixel sizes (match textures.ts). */
const BEAM_TEX_W = 16;
const BEAM_TEX_H = 64;

const BEAM_DEPTH = 5; // at the hazard layer
const FLARE_DEPTH = 6; // endpoint flares + sparks over the beam
const CHARGE_INTERVAL_MS = 60;
const CRACKLE_SPARKS = 3;

interface LState {
  glow: Phaser.GameObjects.Image;
  core: Phaser.GameObjects.Image;
  capA: Phaser.GameObjects.Image;
  capB: Phaser.GameObjects.Image;
}

/** A tiny free-list of images sharing one texture + depth + blend. */
class Pool {
  private readonly free: Phaser.GameObjects.Image[] = [];
  private readonly all: Phaser.GameObjects.Image[] = [];
  constructor(
    private readonly scene: Phaser.Scene,
    private readonly tex: string,
    private readonly depth: number,
  ) {}
  acquire(): Phaser.GameObjects.Image {
    const s = this.free.pop() ?? this.make();
    s.setActive(true).setVisible(true);
    return s;
  }
  release(s: Phaser.GameObjects.Image): void {
    s.setActive(false).setVisible(false).setAngle(0).setScale(1).setAlpha(1).clearTint().setOrigin(0.5, 0.5);
    this.free.push(s);
  }
  private make(): Phaser.GameObjects.Image {
    const s = this.scene.add.image(0, 0, this.tex).setDepth(this.depth).setBlendMode(Phaser.BlendModes.ADD);
    this.all.push(s);
    return s;
  }
  destroyAll(): void {
    for (const s of this.all) s.destroy();
    this.all.length = 0;
    this.free.length = 0;
  }
}

export class LaserFx {
  private readonly scene: Phaser.Scene;
  private readonly glows: Pool;
  private readonly cores: Pool;
  private readonly flares: Pool;
  private readonly state = new Map<number, LState>();
  private readonly lastSpark = new Map<number, number>();
  private emitter?: Phaser.GameObjects.Particles.ParticleEmitter;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.glows = new Pool(scene, HAZARD_TEX.beam, BEAM_DEPTH);
    this.cores = new Pool(scene, HAZARD_TEX.beam, BEAM_DEPTH);
    this.flares = new Pool(scene, HAZARD_TEX.spark, FLARE_DEPTH);
  }

  /** Paint one laser-family segment for the current world time. */
  update(h: SegmentHazard, elapsedMs: number): void {
    const activeStart = h.bornMs + h.telegraphMs;

    let st = this.state.get(h.id);
    if (!st) {
      st = {
        glow: this.glows.acquire(),
        core: this.cores.acquire(),
        capA: this.flares.acquire(),
        capB: this.flares.acquire(),
      };
      this.state.set(h.id, st);
    }

    this.drawBeam(st.glow, h, 2.8);
    this.drawBeam(st.core, h, 1);
    st.capA.setPosition(h.x1, h.y1).setVisible(true);
    st.capB.setPosition(h.x2, h.y2).setVisible(true);

    if (elapsedMs < activeStart) {
      // TELEGRAPH: a thin charging sight-line fading in.
      const p = Phaser.Math.Clamp((elapsedMs - h.bornMs) / Math.max(1, h.telegraphMs), 0, 1);
      st.glow.setTint(HAZARD_COLORS.laserGlow).setAlpha(0.06 + 0.16 * p);
      st.core.setTint(HAZARD_COLORS.laserCore).setAlpha(0.12 + 0.3 * p).setScale(st.core.scaleX * 0.5, st.core.scaleY);
      const flare = HAZARD_COLORS.laserGlow;
      this.flare(st.capA, flare, (0.2 + 0.4 * p) * (h.thickness / 22));
      this.flare(st.capB, flare, (0.2 + 0.4 * p) * (h.thickness / 22));
      return;
    }

    if (segmentBlinkOn(h, elapsedMs)) {
      // ACTIVE + ON: bright pulsing lance + endpoint flares + crackle sparks.
      const pulse = 0.5 + 0.5 * Math.sin(elapsedMs / 40);
      st.glow.setTint(HAZARD_COLORS.laserGlow).setAlpha(0.4 + 0.25 * pulse);
      st.core.clearTint().setAlpha(1);
      this.flare(st.capA, HAZARD_COLORS.laserHot, (0.9 + 0.25 * pulse) * (h.thickness / 18));
      this.flare(st.capB, HAZARD_COLORS.laserHot, (0.9 + 0.25 * pulse) * (h.thickness / 18));
      this.crackle(h, elapsedMs);
    } else {
      // ACTIVE + OFF (blink): faint teal primed line — safe to cross.
      st.glow.setTint(HAZARD_COLORS.turretBody).setAlpha(0.08);
      st.core.setTint(HAZARD_COLORS.turretBody).setAlpha(0.12);
      st.capA.setVisible(false);
      st.capB.setVisible(false);
    }
  }

  /** Release the FX for any laser whose id is no longer live this step. */
  sweep(alive: Set<number>): void {
    for (const [id, st] of this.state) {
      if (alive.has(id)) continue;
      this.release(st);
      this.state.delete(id);
      this.lastSpark.delete(id);
    }
  }

  reset(): void {
    for (const st of this.state.values()) this.release(st);
    this.state.clear();
    this.lastSpark.clear();
    this.emitter?.killAll();
  }

  destroy(): void {
    this.state.clear();
    this.lastSpark.clear();
    this.emitter?.destroy();
    this.emitter = undefined;
    this.glows.destroyAll();
    this.cores.destroyAll();
    this.flares.destroyAll();
  }

  /* --------------------------------------------------------------------- */

  private drawBeam(sprite: Phaser.GameObjects.Image, h: SegmentHazard, widthMul: number): void {
    const dx = h.x2 - h.x1;
    const dy = h.y2 - h.y1;
    const len = Math.hypot(dx, dy);
    sprite
      .setVisible(true)
      .setOrigin(0.5, 0.5)
      .setPosition((h.x1 + h.x2) / 2, (h.y1 + h.y2) / 2)
      .setRotation(Math.atan2(dy, dx) + Math.PI / 2)
      .setScale((h.thickness * widthMul) / BEAM_TEX_W, len / BEAM_TEX_H);
  }

  private flare(sprite: Phaser.GameObjects.Image, tint: number, scale: number): void {
    sprite.setVisible(true).setTint(tint).setScale(Math.max(0.4, scale)).setAlpha(0.9);
  }

  private release(st: LState): void {
    this.glows.release(st.glow);
    this.cores.release(st.core);
    this.flares.release(st.capA);
    this.flares.release(st.capB);
  }

  private ensureEmitter(): Phaser.GameObjects.Particles.ParticleEmitter {
    if (!this.emitter) {
      this.emitter = this.scene.add
        .particles(0, 0, HAZARD_TEX.spark, {
          frequency: -1,
          lifespan: { min: 200, max: 460 },
          speed: { min: 40, max: 160 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.55, end: 0, ease: 'quad.out' },
          alpha: { start: 1, end: 0, ease: 'quad.in' },
          color: [HAZARD_COLORS.laserHot, HAZARD_COLORS.laserCore, HAZARD_COLORS.blastDeep],
          colorEase: 'quad.out',
          blendMode: Phaser.BlendModes.ADD,
          emitting: false,
        })
        .setDepth(FLARE_DEPTH);
      this.emitter.reserve(64);
    }
    return this.emitter;
  }

  /** Crackle a few sparks at points sampled along the live beam (throttled per id). */
  private crackle(h: SegmentHazard, elapsedMs: number): void {
    const bucket = Math.floor(elapsedMs / CHARGE_INTERVAL_MS);
    if (this.lastSpark.get(h.id) === bucket) return;
    this.lastSpark.set(h.id, bucket);
    const e = this.ensureEmitter();
    const samples = 4;
    for (let i = 1; i <= samples; i++) {
      const t = i / (samples + 1);
      e.explode(CRACKLE_SPARKS, h.x1 + (h.x2 - h.x1) * t, h.y1 + (h.y2 - h.y1) * t);
    }
  }
}
